import {BaseType} from 'd3';
import * as d3 from 'd3';
import _ from 'underscore';

import {AlignmentComment} from 'analyses/alignment/AlignmentComments';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {Sequence} from 'components/Sequence';
import * as MapsConstants from 'constants/MapsConstants';
import EditAlignmentComment from 'molbio/maps/linear/EditAlignmentComment';
import LayeredView, {LayeredViewOptions} from 'molbio/maps/linear/LayeredView';
import {AlignmentComments, Layout} from 'molbio/maps/linear/LinearLayout';
import {AlignmentMapState} from 'molbio/maps/MapStates';
import * as mapsutil from 'molbio/maps/MapsUtil';
import {Selection} from 'molbio/maps/selectables/Selection';
import {TabState} from 'Navigation';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface AlignmentCommentsViewOptions extends LayeredViewOptions {
  mapType: string;
}

export default class AlignmentCommentsView extends LayeredView<Sequence, AlignmentComment> {
  layout!: Layout;
  override model!: AlignmentMapState;
  tabstate!: TabState;
  mapType!: string; // 'summary'
  override sequence!: Sequence;
  selection!: Selection;
  editAlignmentView!: EditAlignmentComment;

  static override initClass(): void {
    this.prototype.className = 'alignmentComments';
    this.prototype.setting = vo.ALIGNMENT_COMMENTS.name;
  }

  override getData(): Array<AlignmentComment> {
    return this.model.get('alignmentComments')?.models ?? [];
  }

  override getLayout(): AlignmentComments {
    return this.model.get('layout').AlignmentComments;
  }

  override isShown(): boolean {
    return this.shouldShow();
  }

  override contextMenuOptions(): Array<string> {
    return selectioncontextmenu.ALIGNMENT_COMMENT_MENU;
  }

  override contextMenuData(d: AlignmentComment): {comment: AlignmentComment} {
    return {comment: d};
  }

  override initialize(options: AlignmentCommentsViewOptions): void {
    super.initialize(options);
    this.tabstate = options.tabstate;
    this.layout = this.model.get('layout').AlignmentComments;
    this.mapType = options.mapType;
    this.sequence = this.tabstate.get('mapstates').models[0].get('sequence');
    this.selection = this.sequence.get('selection');
    const editAlignmentCommentOptions = {
      model: this.model,
      layout: this.model.get('layout'),
      selection: this.selection,
      slice: this.slice,
      analysis: this.tabstate.get('analysis'),
    };
    this.editAlignmentView = new EditAlignmentComment(editAlignmentCommentOptions);
    this.editAlignmentView.render();
  }

  override registerListeners(): void {
    super.registerListeners();

    const comments = this.model.get('alignmentComments');
    this.listenTo(comments, 'change:hover change:selected', (comment: AlignmentComment) => {
      this.handleHoverSelect(comment, null);
    });
    this.listenTo(comments, 'add remove change:start change:end change:visible reset', () => {
      this.update();
    });
    if (this.mapType === 'summary') {
      this.listenTo(comments, 'open', (comment: AlignmentComment) => {
        this.open(comment);
      });
    }
  }

  setLayers(): void {
    // set the minimum with for comments to MIN_WIDTH pixels
    const comments = this.getData();
    const MIN_WIDTH = this.getLayout().get('type')!.width(1.5);
    const MIN_BASE_LENGTH = Math.round(this.slice.xInv()(MIN_WIDTH) - this.slice.xInv()(0));
    // eslint-disable-next-line no-restricted-syntax
    _.each(comments, (comment) => {
      const half = Math.round(MIN_BASE_LENGTH / 2);
      const needed = MIN_BASE_LENGTH - (comment.end() - comment.start());
      if (needed > 0) {
        if (comment.start() < this.slice.start() + half) {
          comment.set({layerStart: 0, layerEnd: MIN_BASE_LENGTH});
        } else if (comment.end() > this.slice.end() - MIN_BASE_LENGTH / 2) {
          comment.set({layerStart: this.slice.end() - MIN_BASE_LENGTH, layerEnd: this.slice.end()});
        } else {
          comment.set({
            layerStart: comment.start() - needed / 2,
            layerEnd: comment.end() + needed / 2,
          });
        }
      } else {
        comment.set({layerStart: comment.start(), layerEnd: comment.end()});
      }
    });
    const visibleComments = comments.filter((comment) => comment.get('visible'));
    const modelsAndLayers = mapsutil.getModelLayers(visibleComments, {top: false});
    for (const {model, layer} of modelsAndLayers) {
      model.set(MapsConstants.LINEAR_LAYER_FULL, layer, {silent: true});
    }
    this.model.get('alignmentComments')!.trigger('layerChange');
  }

  override clickAction(e: Event, d: AlignmentComment): void {
    d.click(e, this.tabstate.get('mapstates').models[0].get('sequence'));
  }

  override dblClickAction(e: Event, d: AlignmentComment): void {
    d.dblClick(e, this.tabstate.get('mapstates').models[0].get('sequence'));
  }

  makeLayerPiece(enter: d3.Selection<SVGElement, AlignmentComment, SVGElement, unknown>): void {
    // Call on an enter selector.
    enter.append('svg:path').classed('layer-fill', true).classed('stroked', true);
    enter.append('svg:text');
  }

  override update(trigger: boolean = true): void {
    this.setLayers();
    super.update(trigger);
  }

  rerenderSingle(
    el: BaseType,
    start: number,
    end: number,
    dx: number,
    dy: number,
    rerenderD: AlignmentComment
  ): void {
    d3.select(el)
      .select('path.layer-fill')
      .attr('d', `M 0 0 L 0 ${dy} L ${dx} ${dy} L ${dx} 0 Z`)
      .attr('fill', (d: AlignmentComment) => d3.rgb(d.color()).formatHex())
      .attr('fill-opacity', (d: AlignmentComment) => {
        if (d.get('hover') || d.get('selected')) {
          return 1.0;
        } else {
          return 0.7;
        }
      });
    d3.select(el)
      .selectAll('path.stroked')
      .attr('stroke', (d: AlignmentComment) => {
        if (d.get('selected')) {
          return '#111';
        } else {
          return d3.rgb(d.color()).darker().formatHex();
        }
      })
      .attr('stroke-opacity', 0.7);

    const numChars = this.getLayout().get('type')!.numChars(dx);
    const label = d3.select(el).select('text');
    label
      .attr('x', 0)
      .attr('y', this.getLayout().get('single').height() - 2)
      .attr('font-size', this.getLayout().get('type')!.fontSize)
      .attr('color', 'black')
      .text(rerenderD.text().slice(0, numChars));
  }

  open(comment: AlignmentComment): void {
    this.selection.setEndpoints(comment.start(), comment.end());
    this.editAlignmentView.handle(comment);
    this.$el.closest('.summary-map').append(this.editAlignmentView.el);
  }
}

AlignmentCommentsView.initClass();
